import {
  Editor,
  isNodeSelection,
  isTextSelection,
  posToDOMRect,
} from "@tiptap/core";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import tippy, { Instance, Props } from "tippy.js";
import {
  getSelectedAnchorElement,
  selectAnchorElementURL,
} from "../../../utils/LinkUtils";

export interface BubbleMenuPluginProps {
  pluginKey: PluginKey | string;
  editor: Editor;
  element: HTMLElement;
  tippyOptions?: Partial<Props>;
  tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;
  isModalOpen?: React.MutableRefObject<boolean | undefined>;
  shouldShow?:
    | ((props: {
        editor: Editor;
        view: EditorView;
        state: EditorState;
        oldState?: EditorState;
        from: number;
        to: number;
      }) => boolean)
    | null;
}

export type BubbleMenuViewProps = BubbleMenuPluginProps & {
  view: EditorView;
};

export class BubbleMenuView {
  public editor: Editor;

  public element: HTMLElement;

  public view: EditorView;

  public preventHide = false;

  public forceHide = false;

  public tippy: Instance | undefined;

  public tippyOptions?: Partial<Props>;

  public tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;

  public isModalOpen?: React.MutableRefObject<boolean | undefined>;

  public shouldShow: Exclude<BubbleMenuPluginProps["shouldShow"], null> = ({
    state,
    from,
    to,
  }) => {
    const { doc, selection } = state;
    const { empty } = selection;

    let isLink;

    if (this.isModalOpen.current) return false;

    if ((selection as any)?.node?.type.name === "mention") {
      isLink = selectAnchorElementURL(
        (selection as any)?.node?.attrs.tooltipId,
        this.tooltips.current
      );
    }

    isLink ||= getSelectedAnchorElement();

    // Sometime check for `empty` is not enough.
    // Doubleclick an empty paragraph returns a node size of 2.
    // So we check also for an empty text size.
    const isEmptyTextBlock =
      !doc.textBetween(from, to).length && isTextSelection(state.selection);

    if (isLink) {
      return true;
    }

    if (empty || isEmptyTextBlock) {
      return false;
    }

    return true;
  };

  constructor({
    editor,
    element,
    view,
    tippyOptions = {},
    shouldShow,
    tooltips,
    isModalOpen,
  }: BubbleMenuViewProps) {
    this.editor = editor;
    this.element = element;
    this.tooltips = tooltips;
    this.view = view;
    this.isModalOpen = isModalOpen;

    if (shouldShow) {
      this.shouldShow = shouldShow;
    }

    this.element.addEventListener("mousedown", this.mousedownHandler, {
      capture: true,
    });

    // Controls wether the bubble menu should be
    // displayed while selecting on the email editor.
    document.body.addEventListener("mousedown", this.mousedownHandlerEditor);
    document.body.addEventListener("mouseup", this.mouseupHandlerEditor);

    this.view.dom.addEventListener("dragstart", this.dragstartHandler);
    this.editor.on("focus", this.focusHandler);
    this.editor.on("blur", this.blurHandler);
    this.tippyOptions = tippyOptions;
    // Detaches menu content from its current parent
    this.element.remove();
    this.element.style.visibility = "visible";

    this.update(this.editor.view);

    this.show();
    this.hide();
  }

  mousedownHandler = () => {
    this.preventHide = true;
  };

  mousedownHandlerEditor = (event: MouseEvent) => {
    // Exception to clicks inside the bubble menu itself.
    if (
      this.element &&
      // @ts-ignore
      !this.element.contains(event.target) &&
      this.forceHide === false
    ) {
      this.hide();
    }
    this.forceHide = true;
  };

  mouseupHandlerEditor = (event: MouseEvent) => {
    this.forceHide = false;
    setTimeout(() => this.update(this.editor.view));
  };

  dragstartHandler = () => {
    this.hide();
  };

  focusHandler = () => {
    setTimeout(() => this.update(this.editor.view));
  };

  blurHandler = ({ event }: { event: FocusEvent }) => {
    if (this.preventHide) {
      this.preventHide = false;

      return;
    }

    if (
      event?.relatedTarget &&
      this.element.parentNode?.contains(event.relatedTarget as Node)
    ) {
      return;
    }

    this.hide();
  };

  createTooltip() {
    const { element: editorElement } = this.editor.options;
    const editorIsAttached = !!editorElement.parentElement;

    if (this.tippy || !editorIsAttached) {
      return;
    }

    this.tippy = tippy(editorElement, {
      duration: 0,
      getReferenceClientRect: null,
      content: this.element,
      interactive: true,
      trigger: "manual",
      placement: "top",
      hideOnClick: "toggle",
      popperOptions: {
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              // equivalent to boundary: 'window' in v1, usually NOT necessary in v2
              rootBoundary: "document",
            },
          },
        ],
      },
      offset: [0, 20],
      ...this.tippyOptions,
    });
  }

  update(view: EditorView, oldState?: EditorState) {
    if (this.forceHide) return;

    const { state, composing } = view;
    const { doc, selection } = state;
    const isSame =
      oldState && oldState.doc.eq(doc) && oldState.selection.eq(selection);

    if (composing || isSame) {
      return;
    }

    this.createTooltip();

    // support for CellSelections
    const { ranges } = selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));

    const shouldShow = this.shouldShow?.({
      editor: this.editor,
      view,
      state,
      oldState,
      from,
      to,
    });

    if (!shouldShow) {
      this.hide();

      return;
    }

    this.show();

    this.tippy?.setProps({
      getReferenceClientRect: () => {
        if (isNodeSelection(state.selection)) {
          const node = view.nodeDOM(from) as HTMLElement;

          if (node) {
            return node.getBoundingClientRect();
          }
        }

        try {
          return posToDOMRect(view, from, to);
        } catch (error) {
          this.tippy.hide();
          return null;
        }
      },
    });
  }

  show() {
    this.tippy?.show();
  }

  hide() {
    this.tippy?.hide();
  }

  destroy() {
    this.tippy?.destroy();
    this.element.removeEventListener("mousedown", this.mousedownHandler);
    this.view.dom.removeEventListener("dragstart", this.dragstartHandler);
    document.body.removeEventListener("mousedown", this.mousedownHandlerEditor);
    document.body.removeEventListener("mouseup", this.mouseupHandlerEditor);
    this.editor.off("focus", this.focusHandler);
    this.editor.off("blur", this.blurHandler);
  }
}

export const BubbleMenuPlugin = (options: BubbleMenuPluginProps) => {
  return new Plugin({
    key:
      typeof options.pluginKey === "string"
        ? new PluginKey(options.pluginKey)
        : options.pluginKey,
    view: (view) => new BubbleMenuView({ view, ...options }),
  });
};
